import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Container,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Typography,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  AccountCircle,
  Language,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useIsScrolled } from '../../../hooks';
import { BRAND } from '../../../brand';
import { colors } from '../../theme/colors';
import { logout } from '../../../store/slices/authSlice';
import { useTranslation } from 'react-i18next';
import { v2HeaderSpacerSx } from '../layoutConstants';

const StyledAppBar = styled(motion(AppBar))(({ scrolled }) => ({
  background: scrolled ? 'rgba(247, 248, 250, 0.94)' : 'rgba(247, 248, 250, 0.55)',
  backdropFilter: 'blur(10px)',
  boxShadow: 'none',
  borderBottom: scrolled ? `1px solid ${colors.border.light}` : '1px solid transparent',
  color: colors.text.primary,
  transition: 'background 0.25s ease, border-color 0.25s ease',
}));

const Logo = styled(RouterLink)({
  textDecoration: 'none',
  color: colors.primary.main,
  fontFamily: "'Fraunces', Georgia, serif",
  fontWeight: 600,
  fontSize: '1.35rem',
  letterSpacing: '-0.03em',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});

const NavLink = styled(RouterLink)({
  textDecoration: 'none',
  color: colors.text.primary,
  fontWeight: 500,
  fontSize: '0.92rem',
  padding: '8px 12px',
  borderRadius: 8,
  transition: 'color 0.2s ease, background 0.2s ease',
  '&:hover': {
    color: colors.secondary.main,
    backgroundColor: 'rgba(31,111,120,0.08)',
  },
});

const DesktopNav = styled(Box)(({ theme }) => ({
  display: 'none',
  alignItems: 'center',
  gap: 4,
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const MobileNav = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const NAV_ITEMS = [
  { to: '/v2/packages?category=International', labelKey: 'trips' },
  { to: '/v2/flights', labelKey: 'flights' },
  { to: '/v2/packages?category=Umrah', labelKey: 'umrah' },
  { to: '/v2/packages?category=Ziyarat', labelKey: 'ziyarat' },
  { to: '/v2/customized-booking', labelKey: 'customize' },
  { to: '/v2/help', labelKey: 'help' },
  { to: '/v2/contact', labelKey: 'contact' },
];

const HeaderV2 = () => {
  const { t, i18n } = useTranslation();
  const isScrolled = useIsScrolled(40);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const iconColor = colors.text.primary;
  const displayName =
    user?.firstName ||
    user?.name ||
    user?.email?.split('@')[0] ||
    'My account';

  const handleSignOut = () => {
    dispatch(logout());
    setAccountMenuAnchor(null);
    setMobileOpen(false);
    navigate('/v2');
  };

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    setLangMenuAnchor(null);
    setMobileOpen(false);
  };

  return (
    <>
      <StyledAppBar
        position="fixed"
        scrolled={isScrolled ? 1 : 0}
        elevation={0}
        initial={{ y: -24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45 }}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1.5,
            }}
          >
            <Logo to="/v2">
              <Box
                component="span"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  background: colors.gradient.secondary,
                  flexShrink: 0,
                }}
              />
              {BRAND.name}
            </Logo>

            <DesktopNav>
              {NAV_ITEMS.map((item) => (
                <NavLink key={item.labelKey} to={item.to} state={item.state}>
                  {t(`nav.${item.labelKey}`)}
                </NavLink>
              ))}
              {isAuthenticated ? (
                <Button
                  component={RouterLink}
                  to="/v2/dashboard"
                  size="small"
                  variant="contained"
                  sx={{ ml: 1 }}
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    component={RouterLink}
                    to="/v2/login"
                    size="small"
                    sx={{
                      ml: 1,
                      color: colors.primary.main,
                      borderColor: colors.border.main,
                    }}
                    variant="outlined"
                  >
                    {t('nav.signIn')}
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/v2/register"
                    size="small"
                    sx={{ ml: 0.5 }}
                  >
                    {t('nav.register')}
                  </Button>
                </>
              )}
              <IconButton
                onClick={(e) => setLangMenuAnchor(e.currentTarget)}
                size="small"
                sx={{ color: iconColor, ml: 0.5 }}
              >
                <Language fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={langMenuAnchor}
                open={Boolean(langMenuAnchor)}
                onClose={() => setLangMenuAnchor(null)}
              >
                <MenuItem onClick={() => handleLanguageChange('en')}>English</MenuItem>
                <MenuItem onClick={() => handleLanguageChange('ur')}>اردو</MenuItem>
                <MenuItem onClick={() => handleLanguageChange('ar')}>العربية</MenuItem>
              </Menu>
              <IconButton
                onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
                size="small"
                sx={{ color: iconColor }}
                aria-label="Account menu"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                anchorEl={accountMenuAnchor}
                open={Boolean(accountMenuAnchor)}
                onClose={() => setAccountMenuAnchor(null)}
              >
                {isAuthenticated && (
                  <MenuItem disabled sx={{ opacity: 1, fontWeight: 600 }}>
                    {displayName}
                  </MenuItem>
                )}
                <MenuItem
                  component={RouterLink}
                  to="/v2/dashboard"
                  onClick={() => setAccountMenuAnchor(null)}
                >
                  Dashboard
                </MenuItem>
                <MenuItem
                  component={RouterLink}
                  to="/v2/profile"
                  onClick={() => setAccountMenuAnchor(null)}
                >
                  Profile
                </MenuItem>
                {!isAuthenticated && (
                  <>
                    <Divider />
                    <MenuItem
                      component={RouterLink}
                      to="/v2/login"
                      onClick={() => setAccountMenuAnchor(null)}
                    >
                      Sign in
                    </MenuItem>
                    <MenuItem
                      component={RouterLink}
                      to="/v2/register"
                      onClick={() => setAccountMenuAnchor(null)}
                    >
                      Register
                    </MenuItem>
                  </>
                )}
                {isAuthenticated && (
                  <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
                )}
                <Divider />
                <MenuItem
                  component={RouterLink}
                  to="/v2/help"
                  onClick={() => setAccountMenuAnchor(null)}
                >
                  Help & FAQs
                </MenuItem>
                <MenuItem
                  component={RouterLink}
                  to="/v2/contact"
                  onClick={() => setAccountMenuAnchor(null)}
                >
                  Contact Us
                </MenuItem>
              </Menu>
            </DesktopNav>

            <MobileNav>
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{ color: iconColor }}
                aria-label="Open menu"
              >
                <MenuIcon />
              </IconButton>
            </MobileNav>
          </Box>
        </Container>
      </StyledAppBar>
      <Box component="span" aria-hidden="true" sx={v2HeaderSpacerSx} />

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { width: 280, p: 2 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography
            sx={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600, fontSize: '1.4rem' }}
          >
            {BRAND.name}
          </Typography>
          <IconButton onClick={() => setMobileOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 1 }} />
        <List>
          {NAV_ITEMS.map((item) => (
            <ListItem
              key={item.labelKey}
              button
              component={RouterLink}
              to={item.to}
              state={item.state}
              onClick={() => setMobileOpen(false)}
            >
              <ListItemText primary={t(`nav.${item.labelKey}`)} />
            </ListItem>
          ))}
          {isAuthenticated ? (
            <>
              <ListItem
                button
                component={RouterLink}
                to="/v2/dashboard"
                onClick={() => setMobileOpen(false)}
              >
                <ListItemText primary="Dashboard" />
              </ListItem>
              <ListItem button onClick={handleSignOut}>
                <ListItemText primary="Sign out" />
              </ListItem>
            </>
          ) : (
            <>
              <ListItem
                button
                component={RouterLink}
                to="/v2/login"
                onClick={() => setMobileOpen(false)}
              >
                <ListItemText primary="Sign in" />
              </ListItem>
              <ListItem
                button
                component={RouterLink}
                to="/v2/register"
                onClick={() => setMobileOpen(false)}
              >
                <ListItemText primary="Register" />
              </ListItem>
            </>
          )}
        </List>
      </Drawer>
    </>
  );
};

export default HeaderV2;
